import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { confirmEmailChange } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function ConfirmEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const [newEmail, setNewEmail] = useState('');

  const confirmEmail = useCallback(async () => {
    try {
      const result = await confirmEmailChange(token);
      setStatus('success');
      setNewEmail(result.user?.email || '');
      setMessage('Your email has been changed successfully!');
      
      // Update auth with new token
      if (result.token) {
        login(result.token, result.user);
      }
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Failed to confirm email change. The link may have expired.');
    }
  }, [token, login]);

  useEffect(() => {
    if (token) {
      confirmEmail();
    } else {
      setStatus('error');
      setMessage('Invalid confirmation link');
    }
  }, [token, confirmEmail]);

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#FAFAFA', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '24px',
        padding: '48px',
        maxWidth: '400px',
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
      }}>
        {status === 'loading' && (
          <>
            <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: '#E8553E' }} />
            <h1 className="font-heading text-2xl mb-2" style={{ color: '#1a1a1a' }}>
              Confirming Email Change
            </h1>
            <p style={{ color: '#6b6b6b' }}>Please wait...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-12 h-12 mx-auto mb-4" style={{ color: '#22c55e' }} />
            <h1 className="font-heading text-2xl mb-2" style={{ color: '#1a1a1a' }}>
              Email Changed!
            </h1>
            <p style={{ color: '#6b6b6b', marginBottom: '24px' }}>
              Your email has been updated to <strong>{newEmail}</strong>
            </p>
            <Button 
              onClick={() => navigate('/editor')}
              className="bg-[#1a1a1a] text-white rounded-full px-8"
            >
              Go to Editor
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="w-12 h-12 mx-auto mb-4" style={{ color: '#ef4444' }} />
            <h1 className="font-heading text-2xl mb-2" style={{ color: '#1a1a1a' }}>
              Confirmation Failed
            </h1>
            <p style={{ color: '#6b6b6b', marginBottom: '24px' }}>
              {message}
            </p>
            <Button 
              onClick={() => navigate('/editor')}
              variant="outline"
              className="rounded-full px-8"
            >
              Go to Editor
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
